import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, BellRing, BellOff, Send, ShieldAlert, ShieldCheck, Loader2, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { pushCapability, permissionState, enablePush, disablePush, currentSubscription, isStandalone } from "@/lib/push";
import { Hint } from "@/components/Hint";
import { PushAlertCard } from "@/components/alerts/PushAlertCard";

const Row = ({ title, desc, right, tid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-900 last:border-0" data-testid={tid}>
    <div className="min-w-0"><div className="text-sm font-medium">{title}</div>{desc && <div className="text-xs text-slate-400 mt-0.5">{desc}</div>}</div>
    <div className="shrink-0">{right}</div>
  </div>
);

const permLabel = { granted: ["Allowed", "text-emerald-300", ShieldCheck], denied: ["Blocked in device/browser settings", "text-red-300", ShieldAlert], default: ["Not asked yet", "text-amber-300", ShieldAlert], unsupported: ["Not supported here", "text-slate-400", ShieldAlert] };

export const PushSettings = () => {
  const cap = pushCapability();
  const [perm, setPerm] = useState(permissionState());
  const [status, setStatus] = useState(null);
  const [thisDevice, setThisDevice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/push/status");
      setStatus(r.data);
      const sub = await currentSubscription().catch(() => null);
      setThisDevice(sub ? sub.endpoint : null);
    } catch (e) { console.warn("[apex] request failed:", e?.message || e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribedHere = !!thisDevice && !!status?.subscriptions?.some((s) => s.endpoint === thisDevice);
  const pushOn = status?.prefs?.push_enabled !== false;

  const toggleDevice = async () => {
    setBusy(true);
    try {
      if (subscribedHere) { await disablePush(); toast("Phone notifications turned off on this device"); }
      else { await enablePush(); toast.success("This device will now receive EV-IDEX alerts — even when the app is closed."); }
      setPerm(permissionState());
      await load();
    } catch (e) { toast.error(e?.message || "Could not enable notifications"); setPerm(permissionState()); }
    setBusy(false);
  };

  const setPref = async (patch) => {
    try { const r = await api.put("/push/prefs", patch); setStatus((s) => ({ ...s, prefs: r.data.prefs })); }
    catch (e) { toast.error(apiError(e, "Could not save preference")); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      if (r.data.sent > 0) toast.success(`Test notification sent to ${r.data.sent} device${r.data.sent > 1 ? "s" : ""}. Check your phone's notification shade.`);
      else toast.error(`Push provider did not accept the message (${r.data.failed || 0} failed, ${r.data.removed || 0} expired). Re-enable notifications on this device and try again.`);
      await load();
    } catch (e) { toast.error(apiError(e, "Could not send test notification")); }
    setBusy(false);
  };

  const previewDigest = async () => {
    setBusy(true);
    try { const r = await api.post("/push/digest-preview"); toast.success(r.data.notification.title, { description: r.data.notification.body.slice(0, 180), duration: 10000 }); window.dispatchEvent(new Event("apx:notifications-changed")); await load(); }
    catch (e) { toast.error(apiError(e, "Could not build digest")); }
    setBusy(false);
  };

  const removeDevice = async (endpoint) => { await api.delete("/push/subscribe", { params: { endpoint } }); await load(); };
  const [pl, pc, PIcon] = permLabel[perm] || permLabel.unsupported;

  return (
    <div className="space-y-4" data-testid="push-settings">
      <div className="apx-card p-5">
        <div className="flex items-center gap-2 mb-1"><Smartphone className="w-4 h-4 text-cyan-300" /><Hint k="push_notifications" asChild><h3 className="font-display font-semibold cursor-help">Phone notifications</h3></Hint></div>
        <p className="text-xs text-slate-400 mb-3">Push notifications reach your phone when an alert triggers — even when EV-IDEX is closed — once a device is connected below. Notifications are informational only: EV-IDEX never places a trade when an alert fires.</p>

        {!cap.supported && (
          <div className="rounded-lg border border-amber-900/60 bg-amber-500/10 p-3 text-xs text-amber-200 mb-3" data-testid="push-unsupported-note">{cap.message}</div>
        )}

        <Row tid="push-permission-row" title="Notification permission" desc="Granted by your phone/browser for ev-idex.com"
             right={<span className={`text-xs font-medium inline-flex items-center gap-1 ${pc}`} data-testid="push-permission-state"><PIcon className="w-3.5 h-3.5" /> {pl}</span>} />
        <Row tid="push-device-row" title={subscribedHere ? "This device is connected" : "Connect this device"}
             desc={subscribedHere ? "Alerts will be delivered here in the background." : cap.supported ? (isStandalone() ? "Tap to allow notifications for the EV-IDEX app." : "Tap to allow notifications in this browser. For the most reliable delivery, install EV-IDEX to your home screen.") : "Unavailable on this platform."}
             right={<button data-testid="push-toggle-device" disabled={busy || !cap.supported || perm === "denied"} onClick={toggleDevice}
                            className={`rounded-lg px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${subscribedHere ? "btn-ghost border border-slate-700" : "btn-primary"}`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : subscribedHere ? <BellOff className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5" />}
                      {subscribedHere ? "Disconnect" : "Enable"}
                    </button>} />
        <Row tid="push-master-row" title="Push notifications" desc="Master switch for all your devices. Off = in-app bell only."
             right={<Switch data-testid="push-master-switch" checked={pushOn} onCheckedChange={(v) => setPref({ push_enabled: v })} />} />
        <Row tid="push-test-row" title="Send a test notification" desc={subscribedHere ? "Confirms your phone is connected end-to-end." : "Connect this device first."}
             right={<button data-testid="push-send-test" disabled={busy || !status?.subscriptions?.length} onClick={sendTest} className="rounded-lg px-3 py-2 text-xs font-semibold btn-ghost border border-cyan-900/60 text-cyan-200 inline-flex items-center gap-1.5 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Send test</button>} />
      </div>

      <div className="apx-card p-5">
        <h3 className="font-display font-semibold mb-1">Which alerts reach my phone</h3>
        <p className="text-xs text-slate-400 mb-2">Every alert still appears in the in-app bell. Choose which ones also push to your phone.</p>
        {status?.types && Object.entries(status.types).map(([key, t]) => (
          <Row key={key} tid={`push-type-${key}`} title={t.label} desc={t.desc}
               right={<Switch data-testid={`push-type-switch-${key}`} checked={status.prefs.types[key] !== false} disabled={!pushOn} onCheckedChange={(v) => setPref({ types: { [key]: v } })} />} />
        ))}
      </div>

      <div className="apx-card p-5" data-testid="digest-settings">
        <h3 className="font-display font-semibold mb-1">Evening digest</h3>
        <p className="text-xs text-slate-400 mb-2">Optional once-a-day summary: alerts that triggered or expired in the last 24 h, what is still being watched, and the latest Prediction Accuracy Score. Delivered in-app, and to your phone if the digest type above is on.</p>
        <Row tid="digest-enabled-row" title="Send me an evening digest" desc="Off by default."
             right={<Switch data-testid="digest-enabled-switch" checked={!!status?.prefs?.digest_enabled} onCheckedChange={(v) => setPref({ digest_enabled: v })} />} />
        <Row tid="digest-hour-row" title="Delivery hour (UTC)" desc={`Your local time now: ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · UTC ${new Date().getUTCHours()}:00`}
             right={<select data-testid="digest-hour-select" value={status?.prefs?.digest_hour_utc ?? 18} onChange={(e) => setPref({ digest_hour_utc: Number(e.target.value) })} className="apx-ring px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs">
                      {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>)}
                    </select>} />
        <Row tid="digest-preview-row" title="Preview today's digest" desc="Generates it now (in-app + phone if connected) without affecting the scheduled one."
             right={<button data-testid="digest-preview-btn" disabled={busy} onClick={previewDigest} className="rounded-lg px-3 py-2 text-xs font-semibold btn-ghost border border-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Preview</button>} />
      </div>

      <div className="apx-card p-5">
        <h3 className="font-display font-semibold mb-1">Connected devices</h3>
        {!status?.subscriptions?.length && <div className="text-xs text-slate-500 py-2" data-testid="push-no-devices">No devices connected yet.</div>}
        {status?.subscriptions?.map((s) => (
          <Row key={s.endpoint} tid={`push-device-${s.id}`} title={`${s.platform === "android" ? "Android app" : s.platform === "pwa" ? "Installed app" : "Browser"}${s.endpoint === thisDevice ? " · this device" : ""}`}
               desc={`${(s.user_agent || "").slice(0, 70)} · added ${new Date(s.created_at).toLocaleDateString()}${s.last_sent_at ? ` · last push ${new Date(s.last_sent_at).toLocaleString()}` : ""}${s.failures ? ` · ${s.failures} failed` : ""}`}
               right={<button data-testid={`push-remove-${s.id}`} onClick={() => removeDevice(s.endpoint)} className="btn-ghost rounded-md p-2" aria-label="Remove device"><Trash2 className="w-4 h-4 text-slate-400" /></button>} />
        ))}
        {!!status?.recent_deliveries?.length && (
          <div className="mt-4">
            <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mb-1">Recent phone deliveries</div>
            <div className="space-y-1" data-testid="push-deliveries">
              {status.recent_deliveries.slice(0, 8).map((d) => (
                <div key={d.id} className="text-xs flex items-center gap-2"><span className={d.status === "sent" ? "text-emerald-400" : "text-red-400"}>{d.status}</span><span className="text-slate-400">{d.kind}</span><span className="text-slate-500 ml-auto">{new Date(d.at).toLocaleString()}</span></div>
              ))}
            </div>
          </div>
        )}
      </div>

      <PushAlertCard fcm={status?.fcm} onChange={(fcm) => setStatus((st) => ({ ...st, fcm }))} />

      <div className="text-xs text-slate-500 apx-card p-4" data-testid="push-platform-notes">
        <strong className="text-slate-300">Platform notes:</strong> Android (Chrome or the EV-IDEX app) and desktop Chrome/Edge/Firefox deliver pushes with the app closed. iPhone/iPad deliver pushes only when EV-IDEX is installed to the Home Screen (iOS 16.4+) and opened from there. The native iOS app does not yet support push (requires Apple APNs).
      </div>
    </div>
  );
};
